export const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const routingKeys = ['categoryId', 'subCategoryId', 'masterServiceId', 'childServiceId', 'providerId', 'bookingId', 'mode', 'Id', 'nodeId', 'quickBookServiceId'];

export function parseDeepLink(value) {
  try {
    const url = new URL(value);
    const params = url.searchParams;
    const values = Object.fromEntries(utmKeys.map(key => [key, params.get(key) || '']));
    values.addUtm = utmKeys.some(key => params.has(key));
    let match;
    if ((match = url.pathname.match(/^\/sc\/([^/]+)\/([^/]+)$/))) {
      Object.assign(values, { linkType: 'sub-category', categoryId: decodeURIComponent(match[1]), subCategoryId: decodeURIComponent(match[2]) });
    } else if (url.pathname === '/sub-category' && params.get('categoryId') && params.get('subCategoryId')) {
      Object.assign(values, { linkType: 'sub-category', categoryId: params.get('categoryId'), subCategoryId: params.get('subCategoryId') });
    } else if ((match = url.pathname.match(/^\/cs\/([^/]+)\/([^/]+)$/))) {
      Object.assign(values, { linkType: 'child-service', masterServiceId: decodeURIComponent(match[1]), childServiceId: decodeURIComponent(match[2]) });
    } else if (url.pathname === '/child-service' && params.get('masterServiceId') && params.get('childServiceId')) {
      Object.assign(values, { linkType: 'child-service', masterServiceId: params.get('masterServiceId'), childServiceId: params.get('childServiceId'), categoryId: params.get('categoryId') || '', subCategoryId: params.get('subCategoryId') || '' });
    } else if ((match = url.pathname.match(/^\/p\/([^/]+)$/))) {
      Object.assign(values, { linkType: 'provider', providerId: decodeURIComponent(match[1]) });
    } else if (url.pathname === '/provider' && params.get('providerId')) {
      Object.assign(values, { linkType: 'provider', providerId: params.get('providerId') });
    } else if ((match = url.pathname.match(/^\/b\/([^/]+)$/))) {
      Object.assign(values, { linkType: 'booking', bookingId: decodeURIComponent(match[1]) });
    } else if (url.pathname === '/booking' && params.get('bookingId')) {
      Object.assign(values, { linkType: 'booking', bookingId: params.get('bookingId') });
    } else if (['/qb', '/quick-book'].includes(url.pathname)) {
      const mode = ({ F: 'FIXED_SERVICE', I: 'INSPECTION_FIRST', FIXED_SERVICE: 'FIXED_SERVICE', INSPECTION_FIRST: 'INSPECTION_FIRST' })[params.get('mode')];
      const target = params.get('Id') || params.get('nodeId') || params.get('quickBookServiceId');
      if (!mode || !target) return null;
      Object.assign(values, { linkType: 'quick-book', quickBookMode: mode, quickBookTarget: target, quickBookPath: [] });
    } else return null;
    return values;
  } catch { return null; }
}

const destinationFields = ['linkType', 'categoryId', 'subCategoryId', 'masterServiceId', 'childServiceId', 'providerId', 'bookingId', 'quickBookMode', 'quickBookPath'];
export function sameDeepLinkDestination(values, initial) {
  return !!initial && destinationFields.every(key => key === 'quickBookPath'
    ? JSON.stringify((values[key] || []).filter(Boolean)) === JSON.stringify((initial[key] || []).filter(Boolean))
    : String(values[key] || '') === String(initial[key] || ''));
}

export function findQuickBookPath(nodes, target, prefix = []) {
  for (const node of nodes) {
    const path = [...prefix, node.nodeId];
    if (node.nodeId === target || node.offeringId === target) return path;
    const childPath = findQuickBookPath(node.children || [], target, path);
    if (childPath.length) return childPath;
  }
  return [];
}

export async function loadDeepLinkEditor(record, defaults, services, catalogService) {
  const parsed = parseDeepLink(record.full_url);
  if (!parsed) return { record, supported: false };
  const form = { ...defaults, ...parsed, ...record.edit_configuration,
    short_title: record.title || '', custom_alias: record.short_code || '',
    is_active: record.is_active !== false };
  const lists = { categories: [], subCategories: [], masterServices: [], childServices: [], providers: [], bookings: [], quickBookRoots: [] };
  let unavailable = false;
  const fetchData = async (callback) => {
    try {
      const result = await callback();
      if (Number(result?.success) !== 1) throw new Error('Unavailable');
      return result.data;
    } catch { unavailable = true; return []; }
  };
  const withSaved = (rows, id, key = 'id', label = 'destination') => {
    rows = Array.isArray(rows) ? rows : [];
    if (id && !rows.some(row => String(row[key]) === String(id))) {
      unavailable = true;
      return [...rows, { [key]: id, title: `Saved ${label} #${id} (unavailable)` }];
    }
    return rows;
  };
  if (['sub-category', 'child-service'].includes(form.linkType)) {
    lists.categories = withSaved(await fetchData(() => services.getCategories()), form.categoryId, 'id', 'category');
    if (form.categoryId) lists.subCategories = withSaved(await fetchData(() => services.getSubcategoriesByCategoryId(form.categoryId)), form.subCategoryId, 'id', 'sub-category');
    if (form.linkType === 'child-service') {
      if (!form.categoryId || !form.subCategoryId) unavailable = true;
      const masters = form.subCategoryId ? await fetchData(() => services.getServicesBySubcategory(form.subCategoryId)) : [];
      lists.masterServices = withSaved(masters, form.masterServiceId, 'masterServiceId', 'master service');
      const master = lists.masterServices.find(row => String(row.masterServiceId) === String(form.masterServiceId));
      lists.childServices = withSaved((master?.childServices || []).map(row => ({ ...row, id: row.childServiceId, name: row.title })), form.childServiceId, 'id', 'child service');
    }
  } else if (form.linkType === 'provider') {
    const result = await fetchData(() => services.getProviders({ limit: 100 }));
    lists.providers = withSaved(Array.isArray(result) ? result : result?.experts || result?.providers || result?.data || result?.list || result?.rows || [], form.providerId, 'id', 'expert');
  } else if (form.linkType === 'booking') {
    const result = await fetchData(() => services.getBookings({ limit: 100 }));
    lists.bookings = withSaved(Array.isArray(result) ? result : result?.bookings || result?.data || result?.list || result?.rows || [], form.bookingId, 'id', 'booking');
  } else if (form.linkType === 'quick-book') {
    const result = await fetchData(() => catalogService.catalog(form.quickBookMode));
    lists.quickBookRoots = result?.published?.roots || [];
    form.quickBookPath = findQuickBookPath(lists.quickBookRoots, form.quickBookTarget);
    if (!form.quickBookPath.length) unavailable = true;
  }
  return { record, form, lists, supported: true, warning: unavailable ? 'Some saved destination options are unavailable. You can keep the original destination and edit its settings or UTM values, or choose a replacement destination.' : '' };
}

const queryKey = (token) => {
  try { return decodeURIComponent(token.split('=')[0].replace(/\+/g, ' ')); } catch { return null; }
};

// Keep raw tokens for untouched fields: URLSearchParams would re-encode %20 as +
// and lose the original representation of repeated/unknown query parameters.
export function editDeepLinkUrl(original, values, initial, generated) {
  const sameDestination = sameDeepLinkDestination(values, initial);
  const sameUtm = values.addUtm === initial.addUtm && utmKeys.every(key => (values[key] || '') === (initial[key] || ''));
  if (sameDestination && sameUtm) return original;
  const hashAt = original.indexOf('#');
  const hash = hashAt < 0 ? '' : original.slice(hashAt);
  const raw = hashAt < 0 ? original : original.slice(0, hashAt);
  const queryAt = raw.indexOf('?');
  const originalBase = queryAt < 0 ? raw : raw.slice(0, queryAt);
  let tokens = queryAt < 0 ? [] : raw.slice(queryAt + 1).split('&');
  let base = originalBase;
  if (!sameDestination) {
    const next = new URL(generated);
    base = new URL(original).origin + next.pathname;
    const replacementKeys = [...routingKeys, 'title'];
    tokens = tokens.filter(token => !replacementKeys.includes(queryKey(token)));
    tokens.push(...next.search.slice(1).split('&').filter(token => replacementKeys.includes(queryKey(token))));
  }
  if (!sameUtm) {
    const changedKeys = values.addUtm ? utmKeys.filter(key => !initial.addUtm || (values[key] || '') !== (initial[key] || '')) : utmKeys;
    tokens = tokens.filter(token => !changedKeys.includes(queryKey(token)));
    if (values.addUtm) tokens.push(...changedKeys.filter(key => values[key]).map(key => `${key}=${encodeURIComponent(values[key])}`));
  }
  return base + (tokens.length ? `?${tokens.join('&')}` : '') + hash;
}
